import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useVisitor } from "../context/VisitorAuthContext";
import { TV, TvKicker, TvHeader } from "../components/TriviaTheme";
import { CHART_NAMES } from "../components/ChartRounds";
import { Plus, Minus, Maximize2, Eye, ArrowRight, Trophy, Lock, X, Users } from "lucide-react";

const LABELS = ["A", "B", "C", "D"];
const Btn = "inline-flex items-center justify-center gap-2 font-ui font-semibold rounded-full transition-[transform,background-color,opacity] active:scale-[0.98]";

export default function ChartNight({ kind = "chart" }) {
  const [params] = useSearchParams();
  const region = params.get("region") === "us" ? "us" : "uk";
  const chart = params.get("chart") === "albums" ? "albums" : "singles";
  const isSport = kind === "sport";
  const chartName = isSport ? "Sporting moments" : CHART_NAMES[`${region}-${chart}`];
  const nightName = isSport ? "Sport Night" : "Chart Night";
  const { isMember } = useVisitor();
  const allowed = isMember;
  const [teams, setTeams] = useState([{ name: "Team 1", score: 0 }, { name: "Team 2", score: 0 }]);
  const [per, setPer] = useState(2);
  const [sports, setSports] = useState(SPORTS);
  const [secs, setSecs] = useState(45);
  const [game, setGame] = useState(null);

  const start = async () => {
    const { data } = isSport
      ? await api.get("/trivia/sport-night", { params: { per, sports: sports.join(",") } })
      : await api.get("/trivia/chart-night", { params: { region, chart, per } });
    setGame({ questions: data.questions, idx: 0, intro: true });
  };

  return (
    <Layout>
      <div className={`font-ui ${TV.bg} min-h-screen`} data-testid={isSport ? "sport-night-page" : "chart-night-page"}>
        <TvHeader title="Trivia Vault" subtitle={`Group ${nightName} · host screen`} isMember={isMember} back="/trivia" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-24">
          {!allowed && <Locked />}
          {allowed && !game && (
            <Setup isSport={isSport} sports={sports} setSports={setSports} nightName={nightName} chartName={chartName} teams={teams} setTeams={setTeams} per={per} setPer={setPer} secs={secs} setSecs={setSecs} onStart={start} />
          )}
          {allowed && game && (
            game.idx >= game.questions.length
              ? <Final teams={teams} chartName={chartName} nightName={nightName} onAgain={() => { setTeams(teams.map((t) => ({ ...t, score: 0 }))); setGame(null); }} />
              : <Stage game={game} setGame={setGame} teams={teams} setTeams={setTeams} secs={secs} chartName={chartName} />
          )}
        </div>
      </div>
    </Layout>
  );
}

const Locked = () => (
  <div className={`${TV.card} p-8 text-center max-w-lg mx-auto`} data-testid="chart-night-locked">
    <span className="w-10 h-10 mx-auto rounded-full bg-[#2D4A3E] flex items-center justify-center"><Lock className="w-5 h-5 text-white" /></span>
    <p className="font-ui text-sm sm:text-base text-[#57524B] mt-3">Host mode nights are part of CurioVault Club.</p>
    <Link to="/club" data-testid="chart-night-club" className={`${Btn} ${TV.pillDark} mt-5 px-5 py-2.5 text-sm`}>Join the Club</Link>
  </div>
);

const Choice = ({ value, cur, onClick, testid, children }) => (
  <button data-testid={testid} onClick={() => onClick(value)}
    className={`px-4 py-1.5 rounded-full font-ui text-sm font-semibold border transition-[background-color,color] ${cur === value ? "bg-[#161514] text-white border-[#161514]" : "bg-[#EEE8DD] text-[#4A453E] border-[#DDD5C7] hover:bg-[#E6DFD2]"}`}>
    {children}
  </button>
);

const SPORTS = ["Football", "Cricket", "Rugby", "Olympics", "Athletics", "Tennis", "Golf", "Cycling", "US Sports"];

const SportPicker = ({ sports, setSports }) => {
  const toggle = (sp) => setSports(sports.includes(sp) ? sports.filter((x) => x !== sp) : SPORTS.filter((x) => x === sp || sports.includes(x)));
  return (
    <div data-testid="sport-night-picker">
      <div className="flex items-center justify-between mb-3">
        <p className="tv-caps !text-[11px] text-[#77716A]">Sports in tonight's quiz</p>
        <button type="button" data-testid="sport-picker-all" onClick={() => setSports(sports.length === SPORTS.length ? [] : SPORTS)}
          className="font-ui text-xs font-semibold text-[#2D4A3E] hover:underline">{sports.length === SPORTS.length ? "Clear all" : "Select all"}</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {SPORTS.map((sp) => (
          <button type="button" key={sp} data-testid={`sport-pick-${sp.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => toggle(sp)} aria-pressed={sports.includes(sp)}
            className={`px-4 py-1.5 rounded-full font-ui text-sm font-semibold border transition-[background-color,color] ${sports.includes(sp) ? "bg-[#2D4A3E] text-white border-[#2D4A3E]" : "bg-[#EEE8DD] text-[#4A453E] border-[#DDD5C7] hover:bg-[#E6DFD2]"}`}>
            {sp}
          </button>
        ))}
      </div>
    </div>
  );
};

const Setup = ({ isSport, sports, setSports, nightName, chartName, teams, setTeams, per, setPer, secs, setSecs, onStart }) => {
  const rename = (i, name) => setTeams(teams.map((t, k) => (k === i ? { ...t, name } : t)));
  return (
    <div data-testid="chart-night-setup">
      <TvKicker>Host mode · {chartName}</TvKicker>
      <h1 className="font-tv-serif font-bold text-[#161514] text-[34px] sm:text-5xl leading-[1.02] mt-4">Group {nightName}.</h1>
      <p className="font-ui text-[15px] sm:text-lg text-[#77716A] mt-3 max-w-xl">Put this screen on the telly. {isSport ? "We go sport by sport, from football to US sports." : "We count down through every decade, oldest first."} You read, reveal, and keep score.</p>
      <div className={`${TV.card} p-5 sm:p-6 mt-7 grid sm:grid-cols-2 gap-6`}>
        <div>
          <p className="tv-caps !text-[11px] text-[#77716A] mb-3 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Teams</p>
          <div className="space-y-2">
            {teams.map((t, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid={`team-name-${i}`} value={t.name} onChange={(e) => rename(i, e.target.value)}
                  className="flex-1 min-w-0 rounded-full border border-[#DDD5C7] bg-[#F7F4EE] px-4 py-2 font-ui text-sm text-[#161514] focus:outline-none focus:border-[#2D4A3E]" />
                {teams.length > 1 && (
                  <button data-testid={`team-remove-${i}`} onClick={() => setTeams(teams.filter((_, k) => k !== i))} className={`${Btn} ${TV.pillLight} w-9 h-9`}><X className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
          {teams.length < 8 && (
            <button data-testid="team-add" onClick={() => setTeams([...teams, { name: `Team ${teams.length + 1}`, score: 0 }])} className={`${Btn} ${TV.pillLight} mt-3 px-4 py-2 text-sm`}>
              <Plus className="w-4 h-4" /> Add team
            </button>
          )}
        </div>
        <div className="space-y-5">
          <div>
            <p className="tv-caps !text-[11px] text-[#77716A] mb-3">Questions per {isSport ? "sport" : "decade"}</p>
            <div className="flex gap-2">{[1, 2, 3].map((n) => <Choice key={n} testid={`per-decade-${n}`} value={n} cur={per} onClick={setPer}>{n}</Choice>)}</div>
          </div>
          <div>
            <p className="tv-caps !text-[11px] text-[#77716A] mb-3">Thinking time</p>
            <div className="flex gap-2">{[30, 45, 60].map((n) => <Choice key={n} testid={`timer-${n}`} value={n} cur={secs} onClick={setSecs}>{n}s</Choice>)}</div>
          </div>
          {isSport && <SportPicker sports={sports} setSports={setSports} />}
          <button data-testid="chart-night-start" onClick={onStart} disabled={isSport && !sports.length} className={`${Btn} ${TV.pillDark} w-full py-3 text-sm disabled:opacity-40`}>
            {isSport ? (sports.length ? `Start with ${sports.length} sport${sports.length > 1 ? "s" : ""}` : "Pick at least one sport") : "Start the countdown"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const short = (r) => (/^The \d{4}s$/.test(r) ? `${r.slice(6, 8)}s` : r);

const DecadeRail = ({ rounds, current }) => {
  const at = rounds.indexOf(current);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="decade-rail">
      {rounds.map((d, i) => (
        <span key={d} className={`px-3 py-1 rounded-full font-ui text-xs font-semibold border ${i === at ? "bg-[#2D4A3E] text-white border-[#2D4A3E]" : i < at ? "bg-[#E5EEE8] text-[#2D4A3E] border-[#C7D9CE]" : "bg-[#EEE8DD] text-[#A9A399] border-[#DDD5C7]"}`}>
          {short(d)}
        </span>
      ))}
    </div>
  );
};

const DecadeIntro = ({ round, onDone }) => {
  const [n, setN] = useState(3);
  useEffect(() => {
    if (n <= 0) { onDone(); return; }
    const t = setTimeout(() => setN((x) => x - 1), 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [n]);
  return (
    <div className={`${TV.green} p-10 sm:p-16 text-center tv-rise`} data-testid="decade-intro">
      <p className="tv-caps text-white/70">Next up</p>
      <p className="font-tv-serif font-bold text-6xl sm:text-8xl mt-4" data-testid="decade-intro-label">{round}</p>
      <p key={n} className="font-ui font-bold text-5xl sm:text-7xl mt-8 tabular-nums tv-rise" data-testid="decade-intro-count">{n || "Go"}</p>
    </div>
  );
};

const Scoreboard = ({ teams, setTeams }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5" data-testid="scoreboard">
    {teams.map((t, i) => (
      <div key={i} className={`${TV.card} !rounded-[16px] p-3 flex items-center justify-between gap-2`}>
        <div className="min-w-0">
          <p className="font-ui text-xs font-semibold text-[#77716A] truncate">{t.name}</p>
          <p className="font-ui font-bold text-2xl text-[#161514] tabular-nums" data-testid={`team-score-${i}`}>{t.score}</p>
        </div>
        <div className="flex flex-col gap-1">
          <button data-testid={`team-plus-${i}`} onClick={() => setTeams(teams.map((x, k) => (k === i ? { ...x, score: x.score + 1 } : x)))} className={`${Btn} bg-[#2D4A3E] text-white w-8 h-8`}><Plus className="w-4 h-4" /></button>
          <button data-testid={`team-minus-${i}`} onClick={() => setTeams(teams.map((x, k) => (k === i ? { ...x, score: Math.max(0, x.score - 1) } : x)))} className={`${Btn} ${TV.pillLight} w-8 h-8`}><Minus className="w-4 h-4" /></button>
        </div>
      </div>
    ))}
  </div>
);

const Stage = ({ game, setGame, teams, setTeams, secs, chartName }) => {
  const { questions, idx, intro } = game;
  const q = questions[idx];
  const rounds = [...new Set(questions.map((x) => x.round))];
  const [left, setLeft] = useState(secs);
  const [reveal, setReveal] = useState(null);
  useEffect(() => { setLeft(secs); setReveal(null); }, [idx, secs]);
  useEffect(() => {
    if (intro || reveal || left <= 0) return;
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left, intro, reveal]);

  const doReveal = async () => {
    const { data } = await api.post("/trivia/check", { id: q.id, answer: "" });
    setReveal(data);
  };
  const next = () => {
    const n = idx + 1;
    setGame({ ...game, idx: n, intro: n < questions.length && questions[n].round !== q.round });
  };
  const full = () => document.documentElement.requestFullscreen?.().catch(() => {});

  return (
    <div data-testid="chart-night-stage">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DecadeRail rounds={rounds} current={q.round} />
        <div className="flex items-center gap-2">
          <span className="font-ui text-xs text-[#77716A]" data-testid="chart-night-progress">Q{idx + 1} / {questions.length}</span>
          <button data-testid="chart-night-fullscreen" onClick={full} className={`${Btn} ${TV.pillLight} px-3 py-1.5 text-xs`}><Maximize2 className="w-3.5 h-3.5" /> Big screen</button>
        </div>
      </div>
      <div className="mt-5">
        {intro ? <DecadeIntro round={q.round} onDone={() => setGame({ ...game, intro: false })} /> : (
          <div className={`${TV.card} p-6 sm:p-10 tv-rise`} key={idx} data-testid="chart-night-question">
            <div className="flex items-center justify-between gap-3">
              <p className="tv-caps text-[#57524B]">{q.round} · {chartName}</p>
              <span data-testid="chart-night-timer" className={`font-ui font-bold text-3xl sm:text-4xl tabular-nums ${left <= 10 && !reveal ? "text-[#9E4535]" : "text-[#161514]"}`}>{left}s</span>
            </div>
            <h2 className="font-ui font-bold text-[#161514] text-2xl sm:text-4xl leading-snug mt-5">{q.question}</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-7">
              {q.options.map((o, i) => {
                const hit = reveal && o === reveal.correct_answer;
                return (
                  <div key={i} data-testid={`chart-night-option-${i}`}
                    className={`flex items-center gap-4 rounded-[16px] border px-4 py-4 transition-[background-color,color] ${hit ? "bg-[#2D4A3E] border-[#2D4A3E] text-white" : reveal ? "bg-[#F7F4EE] border-[#ECE6DB] text-[#A9A399]" : "bg-[#F7F4EE] border-[#E4DED2] text-[#161514]"}`}>
                    <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${hit ? "bg-white text-[#2D4A3E]" : "bg-white text-[#57524B] border border-[#DDD5C7]"}`}>{LABELS[i]}</span>
                    <span className="font-ui font-semibold text-lg sm:text-xl">{o}</span>
                  </div>
                );
              })}
            </div>
            {reveal?.fun_fact && <p data-testid="chart-night-fact" className={`${TV.inset} p-4 mt-5 font-ui text-sm sm:text-base text-[#4A453E]`}>{reveal.fun_fact}</p>}
            <div className="mt-6">
              {!reveal
                ? <button data-testid="chart-night-reveal" onClick={doReveal} className={`${Btn} ${TV.pillDark} px-6 py-3 text-sm`}><Eye className="w-4 h-4" /> Reveal answer</button>
                : <button data-testid="chart-night-next" onClick={next} className={`${Btn} ${TV.pillDark} px-6 py-3 text-sm`}>{idx + 1 < questions.length ? "Next question" : "Final standings"} <ArrowRight className="w-4 h-4" /></button>}
            </div>
          </div>
        )}
      </div>
      <Scoreboard teams={teams} setTeams={setTeams} />
    </div>
  );
};

const Final = ({ teams, chartName, nightName, onAgain }) => {
  const ranked = [...teams].sort((a, b) => b.score - a.score);
  const top = ranked[0]?.score ?? 0;
  const winners = ranked.filter((t) => t.score === top);
  return (
    <div data-testid="chart-night-final" className="tv-rise">
      <div className={`${TV.green} p-8 sm:p-12 text-center`}>
        <Trophy className="w-10 h-10 mx-auto text-[#E8D5A6]" />
        <p className="tv-caps text-white/70 mt-4">{chartName} · {nightName}</p>
        <p className="font-tv-serif font-bold text-4xl sm:text-6xl mt-3" data-testid="chart-night-winner">
          {winners.length > 1 ? "It's a tie!" : `${winners[0]?.name} win!`}
        </p>
      </div>
      <div className={`${TV.card} mt-4 divide-y divide-[#ECE6DB]`}>
        {ranked.map((t, i) => (
          <div key={i} className="flex items-center justify-between px-5 py-3 font-ui">
            <span className="font-semibold text-[#161514]">{i + 1}. {t.name}</span>
            <span className="font-bold text-xl tabular-nums text-[#161514]">{t.score}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-5">
        <button data-testid="chart-night-again" onClick={onAgain} className={`${Btn} ${TV.pillDark} px-5 py-2.5 text-sm`}>Play another night</button>
        <Link to="/trivia" className={`${Btn} ${TV.pillLight} px-5 py-2.5 text-sm`}>Back to the Vault</Link>
      </div>
    </div>
  );
};
